import { getTwoColGoodsListData } from './getTwoColGoodsListData';
import type { TwoColGoodsListProps } from './types';
import { GoodsCard2Column } from '@banhala/ui-kit';
import type { GoodsCoreModel, IScreensLogging } from '../../../../api/types';
import { bridge, isNotNil, useNavigator } from '../../../../utils';
import { useLogOnGoodsPressItem } from './log/logOnGoodsPressItem';
import { useLogOnPressLike } from './log/logOnPressLike';
import { useLogDisplayGoods } from './log/useLogDisplayGoods';
import { useEffect } from 'react';
import { useRemoveLikeGoods } from '../../../../queries';
import { useAddLikeGoods } from '../../../../hooks';

export const TwoColGoodsList = ({
  entity,
  likeMap,
  setLikeMap,
}: TwoColGoodsListProps) => {
  const goodsData = getTwoColGoodsListData(entity);
  const { navigator } = useNavigator();
  const logOnPressLike = useLogOnPressLike();
  const logOnGoodsPressItem = useLogOnGoodsPressItem();

  useEffect(() => {
    setLikeMap(prevState => {
      return {
        ...prevState,
        ...entity?.reduce<Record<string, boolean>>((acc, cur) => {
          acc[cur.item.sno] = cur.item.like?.is_liked ?? false;
          return acc;
        }, {}),
      };
    });
  }, [entity, setLikeMap]);

  const { mutateAsync: mutateRemoveLikeGoods } = useRemoveLikeGoods();
  const { logDisplayGoods } = useLogDisplayGoods();
  const { addLikeGoods } = useAddLikeGoods({
    listParams: entity?.[0]?.logging.analytics?.LIST_ID as string | undefined,
    onChangeIsLiked: (sno, nextValue) => {
      setLikeMap(prevState => ({ ...prevState, [sno]: nextValue }));
    },
    screen:
      (entity?.[0]?.logging.analytics?.SCREEN as string | undefined) ?? '',
  });

  const handlePressItem = async ({
    logging,
    item,
  }: {
    logging: IScreensLogging;
    item: GoodsCoreModel;
  }) => {
    logOnGoodsPressItem({ item, logging });
    navigator.moveToGoods({
      sno: item.sno,
      firstPageRendering: item.first_page_rendering ?? null,
      remoteDeeplink: item.remote_deeplink ?? undefined,
      loggingAnalytics: logging.analytics,
    });
  };

  const handlePressLike = async ({
    logging,
    item,
  }: {
    logging: IScreensLogging;
    item: GoodsCoreModel;
  }) => {
    const currentLike = likeMap[item.sno] ?? false;
    logOnPressLike({ item, logging, currentLike });
    if (currentLike) {
      await mutateRemoveLikeGoods({
        sno: item.sno,
        list_params_referrer: logging.analytics?.LIST_ID as string | undefined,
      });
      bridge.likeGoods({
        goods_sno: item.sno,
        is_liked: false,
      });
      setLikeMap(prevState => ({ ...prevState, [item.sno]: false }));
      return;
    }
    addLikeGoods(item.sno, logging.analytics, logging.ably_only_log);
  };

  return (
    <GoodsCard2Column
      likeMap={likeMap}
      listTestID={entity?.[0]?.logging.analytics?.LIST_ID as string | undefined}
      onPressLike={handlePressLike}
      showRanking={
        entity?.some(item => isNotNil(item.render?.data.ranking)) ?? false
      }
      list={goodsData}
      onPressItem={async ({ item, logging }) => {
        handlePressItem({ logging, item });
      }}
      keyExtractor={(item, index) => (item.sno ?? index).toString()}
      onViewableItemsChanged={logDisplayGoods}
    />
  );
};
